import { ref, unref } from 'vue';
import SalesTransactionPartyDocumentsAPI from 'dashboard/api/salesTransactionPartyDocuments';
import {
  uploadSalesPartyDocumentFiles,
  validateSalesPartyDocumentFile,
} from './salesPartyDocumentFiles';

export function useSalesPartyDocuments({ transactionId, partyId, t, notify }) {
  const documents = ref([]);
  const pending = ref([]);
  const loading = ref(false);

  const load = async () => {
    if (!unref(transactionId) || !unref(partyId)) return [];
    loading.value = true;
    try {
      const response = await SalesTransactionPartyDocumentsAPI.list(
        unref(transactionId),
        unref(partyId)
      );
      documents.value = response?.data?.data || [];
      return documents.value;
    } catch (requestError) {
      notify(
        requestError?.response?.data?.message ||
          t('SALES_PARTIES.DOCUMENTS.LOAD_ERROR')
      );
      return [];
    } finally {
      loading.value = false;
    }
  };

  const uploadFile = async (file, documentTypes = []) => {
    const item = {
      key: `${file.name}-${file.size}-${Date.now()}`,
      name: file.name,
      progress: 0,
      error: '',
    };
    pending.value = [...pending.value, item];
    const validationError = validateSalesPartyDocumentFile(file, t);
    if (validationError) {
      item.error = validationError;
      pending.value = [...pending.value];
      return false;
    }

    try {
      const response = await SalesTransactionPartyDocumentsAPI.upload(
        unref(transactionId),
        unref(partyId),
        file,
        event => {
          item.progress = Math.round(
            (event.loaded / (event.total || file.size)) * 100
          );
          pending.value = [...pending.value];
        },
        documentTypes
      );
      const document = response?.data?.data;
      documents.value = [
        ...documents.value.filter(itemValue => itemValue.id !== document?.id),
        document,
      ].filter(Boolean);
      pending.value = pending.value.filter(entry => entry.key !== item.key);
      return true;
    } catch (requestError) {
      item.error =
        requestError?.response?.data?.message ||
        t('SALES_PARTIES.DOCUMENTS.UPLOAD_ERROR');
      pending.value = [...pending.value];
      return false;
    }
  };

  const uploadFiles = async (files, documentTypes = []) => {
    const results = await uploadSalesPartyDocumentFiles(files, file =>
      uploadFile(file, documentTypes)
    );
    if (results.some(Boolean))
      notify(t('SALES_PARTIES.DOCUMENTS.UPLOAD_SUCCESS'));
    return results.some(Boolean);
  };

  const updateTypes = async (document, documentTypes) => {
    try {
      const response = await SalesTransactionPartyDocumentsAPI.update(
        unref(transactionId),
        unref(partyId),
        document.id,
        documentTypes
      );
      documents.value = documents.value.map(item =>
        item.id === document.id ? response?.data?.data || item : item
      );
      return true;
    } catch (requestError) {
      notify(
        requestError?.response?.data?.message ||
          t('SALES_PARTIES.DOCUMENTS.UPDATE_ERROR')
      );
      return false;
    }
  };

  const remove = async document => {
    try {
      await SalesTransactionPartyDocumentsAPI.remove(
        unref(transactionId),
        unref(partyId),
        document.id
      );
      documents.value = documents.value.filter(item => item.id !== document.id);
      notify(t('SALES_PARTIES.DOCUMENTS.REMOVE_SUCCESS'));
      return true;
    } catch (requestError) {
      notify(
        requestError?.response?.data?.message ||
          t('SALES_PARTIES.DOCUMENTS.REMOVE_ERROR')
      );
      return false;
    }
  };

  const download = async document => {
    try {
      const response = await SalesTransactionPartyDocumentsAPI.signedUrl(
        unref(transactionId),
        unref(partyId),
        document.id
      );
      window.open(response.data.data.url, '_blank', 'noopener,noreferrer');
      return true;
    } catch (requestError) {
      notify(
        requestError?.response?.data?.message ||
          t('SALES_PARTIES.DOCUMENTS.DOWNLOAD_ERROR')
      );
      return false;
    }
  };

  return {
    documents,
    download,
    load,
    loading,
    pending,
    remove,
    updateTypes,
    uploadFiles,
  };
}
